import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { BsEnvelopeAt, BsLockFill, BsPersonFill } from "react-icons/bs";
import { AiOutlineEyeInvisible, AiOutlineEye } from "react-icons/ai";
import { useAuth } from "../../contexts/AuthContext";
import Card from "../UI/Card";
import Button from "../UI/Button";
import Input from "../UI/Input";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shineEffect = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const AuthContainer = styled.div`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  animation: ${fadeIn} 0.6s ease-out;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 2rem;
  border-bottom: 1px solid var(--border-color);
  gap: 30px;
  justify-content: center;
  padding: 0 20px;
`;

const Tab = styled.button`
  flex: 0 1 auto;
  min-width: 120px;
  background: transparent;
  border: none;
  padding: 1rem 1.5rem;
  color: ${(props) =>
    props.active ? "var(--primary-color)" : "var(--text-secondary)"};
  border-bottom: 2px solid
    ${(props) => (props.active ? "var(--primary-color)" : "transparent")};
  transition: all 0.3s ease;
  font-weight: 600;
  letter-spacing: 1px;
  font-family: "Playfair Display", serif;
  font-size: 1.2rem;
  cursor: pointer;

  &:hover {
    color: var(--primary-light);
  }

  &::after {
    display: none;
  }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  color: var(--text-tertiary);
  font-size: 0.9rem;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: var(--border-color);
    margin: 0 1rem;
  }
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: var(--background-tertiary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition);
  margin-bottom: 1.5rem;
  font-weight: 500;

  &:hover {
    background: var(--background-secondary);
    transform: translateY(-2px);
    box-shadow: var(--box-shadow);
  }

  svg {
    font-size: 1.5rem;
    margin-right: 0.75rem;
  }

  &::after {
    display: none;
  }
`;

const FormGroup = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const IconWrapper = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 1rem;
  color: var(--text-tertiary);
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PasswordToggle = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 1rem;
  background: transparent;
  border: none;
  color: var(--text-tertiary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    color: var(--text-secondary);
  }

  svg {
    font-size: 1.2rem;
  }

  &::after {
    display: none;
  }
`;

const FormInput = styled(Input)`
  padding-left: 3rem;
`;

const ErrorText = styled.div`
  color: var(--error-color);
  font-size: 0.9rem;
  margin-top: 1.5rem;
  padding: 0.75rem;
  background-color: rgba(231, 76, 60, 0.1);
  border-radius: var(--border-radius);
  border-left: 3px solid var(--error-color);
`;

const SubmitButton = styled(Button)`
  width: 100%;
  margin-top: 1rem;
  background-size: 200% auto;
  animation: ${shineEffect} 3s linear infinite;
  transition: all 0.3s ease;

  &:disabled {
    animation: none;
  }
`;

const ForgotPassword = styled.a`
  display: block;
  text-align: right;
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-top: 0.5rem;

  &:hover {
    color: var(--primary-color);
  }
`;

const CardTitle = styled.h2`
  font-family: "Playfair Display", serif;
  margin-bottom: 0.5rem;
  text-align: center;
  font-size: 1.8rem;
  letter-spacing: 1px;
`;

const CardText = styled.p`
  color: var(--text-secondary);
  text-align: center;
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const ForgotPasswordModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;

  ${(props) =>
    props.isOpen &&
    `
    opacity: 1;
    visibility: visible;
  `}
`;

const ModalContent = styled.div`
  width: 90%;
  max-width: 450px;
  background: var(--background-tertiary);
  border-radius: var(--border-radius);
  padding: 2rem;
  position: relative;
  transform: translateY(-20px);
  transition: transform 0.3s ease;
  border: 1px solid var(--border-color);
  box-shadow: var(--box-shadow);

  ${(props) =>
    props.isOpen &&
    `
    transform: translateY(0);
  `}
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: var(--text-tertiary);
  font-size: 1.2rem;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    color: var(--primary-color);
  }

  &::after {
    display: none;
  }
`;

const ModalTitle = styled.h3`
  margin-bottom: 1rem;
  font-family: "Playfair Display", serif;
  font-size: 1.5rem;
  color: var(--primary-color);
`;

const ModalText = styled.p`
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  color: var(--text-secondary);
`;

const ResetSuccessMessage = styled.div`
  background-color: rgba(46, 204, 113, 0.1);
  border-left: 3px solid var(--success-color);
  padding: 1rem;
  margin-bottom: 1.5rem;
  color: var(--success-color);
  border-radius: var(--border-radius);
`;

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const { login, signup, loginWithGoogle, resetPassword, error, setError } =
    useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setError("");
  }, [isLogin, setError]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!isLogin && password !== confirmPassword) {
      return setError("Passwords do not match");
    }

    try {
      setLoading(true);
      if (isLogin) {
        await login(email, password);
      } else {
        await signup(email, password, name);
      }
      navigate("/");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    try {
      setError("");
      setLoading(true);
      await loginWithGoogle();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    if (!resetEmail) {
      setError("Please enter your email address");
      return;
    }

    try {
      setError("");
      setLoading(true);
      await resetPassword(resetEmail);
      setResetSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function closeForgotPassword() {
    setForgotPasswordOpen(false);
    setResetEmail("");
    setResetSent(false);
  }

  return (
    <AuthContainer>
      <Card>
        <Card.Body>
          <CardTitle>{isLogin ? "Welcome Back" : "Create Account"}</CardTitle>
          <CardText>
            {isLogin
              ? "Sign in to access your account"
              : "Join us and start creating amazing scripts"}
          </CardText>

          <TabContainer>
            <Tab active={isLogin} onClick={() => setIsLogin(true)}>
              Login
            </Tab>
            <Tab active={!isLogin} onClick={() => setIsLogin(false)}>
              Sign Up
            </Tab>
          </TabContainer>

          <GoogleButton onClick={handleGoogleSignIn} disabled={loading}>
            <FcGoogle />
            Continue with Google
          </GoogleButton>

          <OrDivider>OR</OrDivider>

          <form onSubmit={handleSubmit}>
            {!isLogin && (
              <FormGroup>
                <IconWrapper>
                  <BsPersonFill />
                </IconWrapper>
                <FormInput
                  placeholder="Your Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </FormGroup>
            )}

            <FormGroup>
              <IconWrapper>
                <BsEnvelopeAt />
              </IconWrapper>
              <FormInput
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <IconWrapper>
                <BsLockFill />
              </IconWrapper>
              <FormInput
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
              </PasswordToggle>
            </FormGroup>

            {!isLogin && (
              <FormGroup>
                <IconWrapper>
                  <BsLockFill />
                </IconWrapper>
                <FormInput
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                </PasswordToggle>
              </FormGroup>
            )}

            {isLogin && (
              <ForgotPassword
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setForgotPasswordOpen(true);
                  setResetEmail(email); // Pre-populate with the email if entered
                }}
              >
                Forgot your password?
              </ForgotPassword>
            )}

            {error && <ErrorText>{error}</ErrorText>}

            <SubmitButton disabled={loading}>
              {loading
                ? "Processing..."
                : isLogin
                ? "Sign In"
                : "Create Account"}
            </SubmitButton>
          </form>
        </Card.Body>
      </Card>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal isOpen={forgotPasswordOpen}>
        <ModalContent isOpen={forgotPasswordOpen}>
          <CloseButton onClick={closeForgotPassword}>&times;</CloseButton>
          <ModalTitle>Reset Password</ModalTitle>

          {resetSent ? (
            <>
              <ResetSuccessMessage>
                Password reset instructions have been sent to your email
                address.
              </ResetSuccessMessage>
              <ModalText>
                Please check your inbox and follow the instructions to reset
                your password. If you don't see it, please check your spam
                folder.
              </ModalText>
              <SubmitButton onClick={closeForgotPassword}>Close</SubmitButton>
            </>
          ) : (
            <>
              <ModalText>
                Enter your email address and we'll send you instructions to
                reset your password.
              </ModalText>

              <form onSubmit={handleResetPassword}>
                <FormGroup>
                  <IconWrapper>
                    <BsEnvelopeAt />
                  </IconWrapper>
                  <FormInput
                    type="email"
                    placeholder="Email Address"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                  />
                </FormGroup>

                {error && <ErrorText>{error}</ErrorText>}

                <SubmitButton type="submit" disabled={loading}>
                  {loading ? "Sending..." : "Send Reset Instructions"}
                </SubmitButton>
              </form>
            </>
          )}
        </ModalContent>
      </ForgotPasswordModal>
    </AuthContainer>
  );
};

export default AuthPage;
 